import { useKeycloak, ReactKeycloakProvider } from '@react-keycloak/web';
export { useKeycloak } from '@react-keycloak/web';
import * as React from 'react';
import React__default, { useMemo, useCallback, Suspense } from 'react';
import Keycloak from 'keycloak-js';
import { initReactI18next, I18nextProvider, useTranslation } from 'react-i18next';
import { BrowserRouter, Link, Routes, Route } from 'react-router-dom';
import i18n from 'i18next';
import Backend from 'i18next-xhr-backend';
import LanguageDetector from 'i18next-browser-languagedetector';
import { QueryClientProvider } from 'react-query';
import { Box, Typography } from '@mui/material';
import { makeG2STyles } from '@smartb/g2-themes';
import { Button } from '@smartb/g2-components';
import { Provider } from 'react-redux';
import { combineReducers, createStore, applyMiddleware, compose } from 'redux';

function useAuth(roles = [], additionnalServices) {
    const { keycloak } = useKeycloak();
    const keycloakWithRoles = useMemo(() => keycloak, [keycloak]);
    const getPrincipalRole = useCallback((givenRoles) => {
        for (let it in roles) {
            if (givenRoles.includes(roles[it])) {
                return roles[it];
            }
        }
        return;
    }, [roles]);
    const getUserPrincipalRole = useCallback(() => {
        var _a, _b, _c;
        //@ts-ignore
        const userRoles = (_c = (_b = (_a = keycloakWithRoles.tokenParsed) === null || _a === void 0 ? void 0 : _a.realm_access) === null || _b === void 0 ? void 0 : _b.roles) !== null && _c !== void 0 ? _c : [];
        return getPrincipalRole(userRoles);
    }, [keycloakWithRoles, getPrincipalRole]);
    const hasRole = useCallback((role) => {
        if (Array.isArray(role)) {
            for (let it in role) {
                if (keycloakWithRoles.hasRealmRole(role[it]))
                    return true;
            }
            return false;
        }
        else {
            return keycloakWithRoles.hasRealmRole(role);
        }
    }, [keycloakWithRoles]);
    const getUserId = useCallback(
    // @ts-ignore
    () => { var _a; return (_a = keycloakWithRoles.tokenParsed) === null || _a === void 0 ? void 0 : _a.sub; }, [keycloakWithRoles]);
    const getUser = useCallback(
    // @ts-ignore
    () => {
        var _a, _b, _c, _d, _e;
        if (!keycloakWithRoles.authenticated)
            return;
        return {
            //@ts-ignore
            id: (_a = keycloakWithRoles.tokenParsed) === null || _a === void 0 ? void 0 : _a.sub,
            //@ts-ignore
            email: (_b = keycloakWithRoles.tokenParsed) === null || _b === void 0 ? void 0 : _b.email,
            //@ts-ignore
            organizationId: (_c = keycloakWithRoles.tokenParsed) === null || _c === void 0 ? void 0 : _c.memberOf,
            //@ts-ignore
            firstName: (_d = keycloakWithRoles.tokenParsed) === null || _d === void 0 ? void 0 : _d.given_name,
            //@ts-ignore
            lastName: (_e = keycloakWithRoles.tokenParsed) === null || _e === void 0 ? void 0 : _e.family_name
        };
    }, [keycloakWithRoles]);
    const rolesServices = useMemo(() => {
        const object = {};
        for (let it in roles) {
            const fn = () => hasRole(roles[it]);
            object[`is_${roles[it]}`] = fn;
        }
        return object;
    }, [hasRole, roles]);
    const service = useMemo(() => (Object.assign({ getUserId: getUserId, hasRole: hasRole, getUser: getUser, getUserPrincipalRole: getUserPrincipalRole, getPrincipalRole: getPrincipalRole }, rolesServices)), [
        hasRole,
        getUserId,
        getUser,
        getUserPrincipalRole,
        rolesServices,
        getPrincipalRole
    ]);
    const additionnals = useMemo(() => {
        const object = {};
        for (let serviceName in additionnalServices) {
            const fn = (params) => additionnalServices[serviceName.toString()](keycloakWithRoles, service, params);
            object[serviceName.toString()] = fn;
        }
        return object;
    }, [additionnalServices, keycloakWithRoles, hasRole, roles, service]);
    return {
        service: Object.assign(service, additionnals),
        keycloak: keycloakWithRoles
    };
}

let keycloakSingleton;
const instanceBuilder = (config, initOptions) => {
    keycloakSingleton = {
        instance: Keycloak(config),
        initOptions: initOptions || initOptionsDefault
    };
    return keycloakSingleton;
};
const initOptionsDefault = {
    checkLoginIframe: false,
    onLoad: 'login-required'
};
const KeycloakAuthFactory = (config, initOption) => keycloakSingleton || instanceBuilder(config, initOption);
const keycloakAuth = () => keycloakSingleton;

const KeycloakProvider = (props) => {
    const { config, initOptions, loadingComponent, children, instance } = props;
    const instanceMemo = useMemo(() => {
        return instance || KeycloakAuthFactory(config, initOptions);
    }, [config]);
    return (React__default.createElement(ReactKeycloakProvider, { authClient: instanceMemo.instance, LoadingComponent: loadingComponent, initOptions: instanceMemo.initOptions }, children));
};

const initI18next = (languages, options) => {
    let fallbackLng = {};
    if (!languages) {
        fallbackLng = {
            'fr-FR': ['fr'],
            default: ['fr']
        };
    }
    else {
        let iteration = 0;
        for (var languageName in languages) {
            fallbackLng[languages[languageName.toString()]] = [languageName];
            if (iteration === 0) {
                fallbackLng['default'] = [languageName];
            }
            iteration++;
        }
    }
    i18n
        .use(Backend)
        .use(LanguageDetector)
        .use(initReactI18next)
        // init i18next
        // for all options read: https://www.i18next.com/overview/configuration-options
        .init(Object.assign({ fallbackLng: fallbackLng, interpolation: {
            escapeValue: false // not needed for react as it escapes by default
        } }, options));
    return i18n;
};

const AppProvider = (props) => {
    const { i18nOverride, languages = { fr: 'fr-FR' }, loadingComponent, queryClient, children } = props;
    const i18n = useMemo(() => i18nOverride || initI18next(languages), [i18nOverride, languages]);
    return (React__default.createElement(QueryClientProvider, { client: queryClient },
        React__default.createElement(I18nextProvider, { i18n: i18n },
            React__default.createElement(Suspense, { fallback: loadingComponent },
                React__default.createElement(BrowserRouter, null, children)))));
};

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

var _g, _defs;

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

var Svg404 = function Svg404(props) {
  return /*#__PURE__*/React.createElement("svg", _extends({
    viewBox: "0 0 1774 865",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _g || (_g = /*#__PURE__*/React.createElement("g", {
    filter: "url(#404_svg__a)"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M.55 626.18 243.716 8.016h150.879v585.449h77.148v99.121h-77.148V842H276.918V692.586H.551V626.18Zm276.368-32.715V470.418l11.719-314.453h-14.649l-144.531 437.5h147.461Zm267.461-347.168c0-36.458 3.255-67.871 9.766-94.238 6.835-26.693 15.625-48.991 26.367-66.895 11.067-18.23 23.763-32.878 38.086-43.945 14.323-11.068 28.971-19.532 43.945-25.39 15.299-6.186 30.436-10.255 45.41-12.208 15.3-1.953 29.297-2.93 41.992-2.93 12.045 0 25.391.977 40.039 2.93 14.974 1.628 29.948 5.371 44.922 11.23 15.3 5.534 29.948 13.835 43.946 24.903 14.322 10.742 26.855 25.228 37.597 43.457 11.068 18.229 19.857 40.69 26.367 67.383 6.836 26.692 10.254 58.594 10.254 95.703v352.539c0 36.458-3.418 68.034-10.254 94.726-6.51 26.368-15.299 48.829-26.367 67.383-11.067 18.555-23.763 33.692-38.086 45.41-14.323 11.394-28.971 20.346-43.945 26.856-14.974 6.185-29.948 10.417-44.922 12.695-14.648 2.279-27.832 3.418-39.551 3.418-13.021 0-27.181-1.139-42.48-3.418-15.3-2.278-30.599-6.51-45.899-12.695-15.299-6.51-30.11-15.462-44.433-26.856-13.998-11.393-26.367-26.367-37.11-44.921-10.742-18.555-19.368-41.179-25.878-67.872-6.511-26.692-9.766-58.268-9.766-94.726V246.297Zm129.394 405.762c0 34.505 6.185 59.57 18.555 75.195 12.695 15.625 31.901 23.437 57.617 23.437 25.065 0 43.783-7.812 56.153-23.437 12.369-15.625 18.554-40.69 18.554-75.195V200.398c0-36.458-6.185-62.988-18.554-79.589-12.37-16.602-31.088-24.903-56.153-24.903-25.716 0-44.922 8.301-57.617 24.903-12.37 16.601-18.555 43.131-18.555 79.589v451.661Zm365.607-25.879L1282.54 8.016h150.88v585.449h77.15v99.121h-77.15V842h-117.67V692.586h-276.37V626.18Zm276.37-32.715V470.418l11.71-314.453h-14.64l-144.53 437.5h147.46Zm296.27 102.539h145.99V842h-145.99V696.004Z",
    fill: "#353945"
  }))), _defs || (_defs = /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("filter", {
    id: "404_svg__a",
    x: 0.551,
    y: 0.691,
    width: 1772.46,
    height: 863.633,
    filterUnits: "userSpaceOnUse",
    colorInterpolationFilters: "sRGB"
  }, /*#__PURE__*/React.createElement("feFlood", {
    floodOpacity: 0,
    result: "BackgroundImageFix"
  }), /*#__PURE__*/React.createElement("feColorMatrix", {
    "in": "SourceAlpha",
    values: "0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
  }), /*#__PURE__*/React.createElement("feOffset", {
    dx: 15,
    dy: 15
  }), /*#__PURE__*/React.createElement("feColorMatrix", {
    values: "0 0 0 0 0.575 0 0 0 0 0.575 0 0 0 0 0.575 0 0 0 0.5 0"
  }), /*#__PURE__*/React.createElement("feBlend", {
    in2: "BackgroundImageFix",
    result: "effect1_dropShadow"
  }), /*#__PURE__*/React.createElement("feBlend", {
    "in": "SourceGraphic",
    in2: "effect1_dropShadow",
    result: "shape"
  })))));
};

const NotFoundIcon = React__default.forwardRef((props, ref) => {
    return React__default.createElement(Svg404, Object.assign({ ref: ref }, props));
});

const useStyles = makeG2STyles()((theme) => ({
    notFoundIcon: {
        maxWidth: '400px',
        width: '80vw'
    },
    title: {
        margin: theme.spacing * 2,
        '@media (max-width:800px)': {
            fontSize: '35px'
        },
        '@media (max-width:400px)': {
            fontSize: '30px'
        }
    }
}));
const NoMatchPage = (props) => {
    const { title = 'Page not found', buttonLabel = 'Go back home', noGoBack = false, classes, styles, className, style, id } = props, other = __rest(props, ["title", "buttonLabel", "noGoBack", "classes", "styles", "className", "style", "id"]);
    const defaultStyles = useStyles();
    return (React__default.createElement(Box, Object.assign({ className: className, style: style, id: id, display: 'flex', width: '100%', height: '100%', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '20px' }, other),
        React__default.createElement(NotFoundIcon, { className: defaultStyles.cx(defaultStyles.classes.notFoundIcon, classes === null || classes === void 0 ? void 0 : classes.notFoundIcon), style: styles === null || styles === void 0 ? void 0 : styles.notFoundIcon }),
        React__default.createElement(Typography, { variant: 'h3', className: defaultStyles.cx(defaultStyles.classes.title, classes === null || classes === void 0 ? void 0 : classes.title), style: styles === null || styles === void 0 ? void 0 : styles.title }, title),
        !noGoBack && (React__default.createElement(Button, { className: classes === null || classes === void 0 ? void 0 : classes.backButton, style: styles === null || styles === void 0 ? void 0 : styles.backButton, component: Link, componentProps: { to: '/' }, variant: 'outlined' }, buttonLabel))));
};

const Router = (props) => {
    const { children, noMatchComponent, noMatchPageProps } = props, other = __rest(props, ["children", "noMatchComponent", "noMatchPageProps"]);
    return (React__default.createElement(Routes, Object.assign({}, other),
        children,
        React__default.createElement(Route, { path: '*', element: noMatchComponent || React__default.createElement(NoMatchPage, Object.assign({}, noMatchPageProps)) })));
};

const PrivateRoute = (props) => {
    const { roles, unauthorizedComponent } = props, other = __rest(props, ["roles", "unauthorizedComponent"]);
    const { service } = useAuth();
    return service.hasRole(roles) ? (React__default.createElement(Route, Object.assign({}, other))) : (unauthorizedComponent || React__default.createElement(NoMatchPage, null));
};

const useI18n = () => {
    const { i18n } = useTranslation();
    return { i18n: i18n };
};

const i2Config = () => {
    var _a, _b;
    // @ts-ignore
    return (_b = (_a = window._env_) === null || _a === void 0 ? void 0 : _a.config) === null || _b === void 0 ? void 0 : _b.i2;
};
const fsConfig = () => {
    var _a, _b;
    // @ts-ignore
    return (_b = (_a = window._env_) === null || _a === void 0 ? void 0 : _a.config) === null || _b === void 0 ? void 0 : _b.fs;
};
const G2ConfigBuilder = (config) => {
    var _a;
    // @ts-ignore
    window._env_ = Object.assign(Object.assign({}, window._env_), { config: Object.assign(Object.assign({}, (_a = window._env_) === null || _a === void 0 ? void 0 : _a.config), config) });
};
const g2Config = () => {
    var _a;
    // @ts-ignore
    return (_a = window._env_) === null || _a === void 0 ? void 0 : _a.config;
};

const ReduxProvider = (props) => {
    const { reduxStore, children } = props;
    return React__default.createElement(Provider, { store: reduxStore }, children);
};

/**
 * initiliaze the instance of the redux store
 * @param {Reducers extends { [key: string]: Reducer<any, AnyAction> }}  reducers - an object containing the reducers you want to insert in your store like that: `{myReducer: myReducerObject}`
 * @param {Middleware<{}, any, any>[]}  middlewares - An array containing the possible middlewares you want to add to your store
 * @return {Store} Return the redux store instance
 */
const initRedux = (reducers, middlewares) => {
    middlewares = middlewares !== null && middlewares !== void 0 ? middlewares : [];
    const reducersCombination = reducers !== null && reducers !== void 0 ? reducers : {};
    const reducer = combineReducers(reducersCombination);
    const composeEnhancer = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;
    const store = createStore(reducer, composeEnhancer(applyMiddleware(...middlewares)));
    return { store: store, reducer: reducer };
};

export { AppProvider, G2ConfigBuilder, KeycloakAuthFactory, KeycloakProvider, NoMatchPage, PrivateRoute, ReduxProvider, Router, fsConfig, g2Config, i2Config, initI18next, initRedux, keycloakAuth, useAuth, useI18n };
//# sourceMappingURL=index.es.js.map
